import React, { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import {
    AxisScrollStrategies,
    emptyTick,
    lightningChart,
    AxisTickStrategies,
    SolidFill,
    SolidLine,
    ColorRGBA,
    IndividualPointFill,
    FontSettings,
    PointShape,
    Themes
} from "@lightningchart/lcjs";
import { DatePicker, InputGroup } from 'rsuite';
import { startOfDay, endOfDay, sub } from 'date-fns';

import "./DetectionManagement.scss";
import '../../styles/rsuite-no-reset.css';
import DetectionServices from "../../services/api/DetectionServices";
import NewDetectionConfig from './NewDetectionConfig';
import { useAuthContext } from '../../dataproviders/AuthDataProvider';
import { useDetectionContext } from "../../dataproviders/DetectionDataProvider";
import { useNavigate } from "react-router-dom/dist";

function DetectionManagement() {
    const authContext = useAuthContext();
    const detectionContext = useDetectionContext();
    const navigate = useNavigate();

    const [utcNow] = useState(new Date(new Date().toUTCString().slice(0, -3)));

    const handleRefreshClusteringEventsRef = useRef();
    const midPointsRef = useRef([]);
    const lowPointsRef = useRef([]);
    const midOverlayRef = useRef([]);
    const lowOverlayRef = useRef([]);

    // #region ----- LightningChart const -----
    const highlightCETD1 = useMemo(() => new SolidFill({ color: ColorRGBA(13, 90, 154, 80) }), []);
    const highlightCETD2 = useMemo(() => new SolidFill({ color: ColorRGBA(248, 126, 123, 80) }), []);
    const highlightCETD3 = useMemo(() => new SolidFill({ color: ColorRGBA(0, 177, 143, 80) }), []);
    const highlightCETD4 = useMemo(() => new SolidFill({ color: ColorRGBA(255, 127, 0, 80) }), []);
    const strokeCETD1 = useMemo(() => new SolidFill({ color: ColorRGBA(13, 90, 154) }), []);
    const strokeCETD2 = useMemo(() => new SolidFill({ color: ColorRGBA(248, 126, 123) }), []);
    const strokeCETD3 = useMemo(() => new SolidFill({ color: ColorRGBA(0, 177, 143) }), []);
    const strokeCETD4 = useMemo(() => new SolidFill({ color: ColorRGBA(255, 127, 0) }), []);
    // #endregion

    useEffect(() => {
        // #region ----- Chart Spectrogram methods to process wave form -----
        let license = {};
        const _auth = JSON.parse(sessionStorage.getItem("auth"));

        if (!!_auth) {
            if (_auth.license !== undefined && _auth.license !== 'undefined' && _auth.license !== '' &&
                _auth.apptitle !== undefined && _auth.apptitle !== 'undefined' && _auth.apptitle !== '' &&
                _auth.company !== undefined && _auth.company !== 'undefined' && _auth.company !== '') {
                license = {
                    license: _auth.license,
                    licenseInformation: {
                        appTitle: _auth.apptitle,
                        company: _auth.company
                    }
                }
            }
        }
        // #endregion

        // #region ----- Config -----
        // #region ----- Chart Clustering extra config -- onMouseClick -----
        const onMouseDetectionClick = (obj, event) => {
            detectionContext.SetClusterLink(prevState => ({
                ...prevState,
                linkId: obj.getName(),
                linkType: 'detection'
            }));

            navigate("/detections");
        };

        const onMouseLeaveClust = (obj, e) => {
            const tooltip = document.getElementById('clustering-chart-tooltip');

            if (tooltip) {
                tooltip.style.display = 'none';
            }
        };

        const onMouseMoveClust = (obj, e) => {
            const newStartDate = new Date(obj.getValueStart());
            const sDyear = newStartDate.getFullYear();
            const sDmonth = String(newStartDate.getMonth() + 1).padStart(2, '0');
            const sDday = String(newStartDate.getDate()).padStart(2, '0');
            const sDhours = String(newStartDate.getHours()).padStart(2, '0');
            const sDminutes = String(newStartDate.getMinutes()).padStart(2, '0');
            const sDseconds = String(newStartDate.getSeconds()).padStart(2, '0');

            const formattedStartDate = `${sDyear}-${sDmonth}-${sDday}T${sDhours}:${sDminutes}:${sDseconds}`;

            const newEndDate = new Date(obj.getValueEnd());
            const eDyear = newEndDate.getFullYear();
            const eDmonth = String(newEndDate.getMonth() + 1).padStart(2, '0');
            const eDday = String(newEndDate.getDate()).padStart(2, '0');
            const eDhours = String(newEndDate.getHours()).padStart(2, '0');
            const eDminutes = String(newEndDate.getMinutes()).padStart(2, '0');
            const eDseconds = String(newEndDate.getSeconds()).padStart(2, '0');

            const formattedEndDate = `${eDyear}-${eDmonth}-${eDday}T${eDhours}:${eDminutes}:${eDseconds}`;

            const rect = e.srcElement.getBoundingClientRect();
            let height = 0;

            //only applicable for bottom clustering chart
            if (e.currentTarget.id === "low-clustering-chart") {
                height = e.srcElement.getBoundingClientRect().height + 50;
            }

            const mouseX = e.clientX;
            const mouseY = e.clientY - rect.top + height;

            const tooltip = document.getElementById('clustering-chart-tooltip');
            // Show tooltip
            document.getElementById('clustering-chart-tooltip-start').innerHTML = formattedStartDate;
            document.getElementById('clustering-chart-tooltip-end').innerHTML = formattedEndDate;

            if (tooltip) {
                tooltip.style.left = `${mouseX}px`;
                tooltip.style.top = `${mouseY}px`;
                tooltip.style.display = 'block';
            }

        };

        const onMouseClickClust = (obj) => {
            const newStartDate = new Date(obj.getValueStart());
            const sDyear = newStartDate.getFullYear();
            const sDmonth = String(newStartDate.getMonth() + 1).padStart(2, '0');
            const sDday = String(newStartDate.getDate()).padStart(2, '0');
            const sDhours = String(newStartDate.getHours()).padStart(2, '0');
            const sDminutes = String(newStartDate.getMinutes()).padStart(2, '0');
            const sDseconds = String(newStartDate.getSeconds()).padStart(2, '0');

            const formattedStartDate = `${sDyear}-${sDmonth}-${sDday}T${sDhours}:${sDminutes}:${sDseconds}`;

            const newEndDate = new Date(obj.getValueEnd());
            const eDyear = newEndDate.getFullYear();
            const eDmonth = String(newEndDate.getMonth() + 1).padStart(2, '0');
            const eDday = String(newEndDate.getDate()).padStart(2, '0');
            const eDhours = String(newEndDate.getHours()).padStart(2, '0');
            const eDminutes = String(newEndDate.getMinutes()).padStart(2, '0');
            const eDseconds = String(newEndDate.getSeconds()).padStart(2, '0');

            const formattedEndDate = `${eDyear}-${eDmonth}-${eDday}T${eDhours}:${eDminutes}:${eDseconds}`;

            detectionContext.SetClusterLink(prevState => ({
                ...prevState,
                linkId: obj.getName(),
                linkType: 'cluster',
                clusterStart: formattedStartDate,
                clusterEnd: formattedEndDate,
                clusterId: obj.getName()
            }));

            navigate("/detections");
        };

        const userOffset = new Date().getTimezoneOffset() * 60 * 1000;
        // #endregion
        // #region ----- Chart Low Clustering primary config -----
        const chartLowClust = lightningChart(license)
            .ChartXY({
                theme: Themes.light,
                // Set the chart into a div with id, 'target'.
                // Chart's size will automatically adjust to div's size.
                container: 'low-clustering-chart',
                defaultAxisX: {
                    type: 'linear-highPrecision'
                }
            })
            .setTitle('Low-Frequency')
            .setTitleFillStyle(new SolidFill({ color: ColorRGBA(48, 50, 53) }))
            .setTitleFont(new FontSettings({ size: 12, style: 'bold' }))
            .setTitleMargin({ left: 25 })
            .setBackgroundFillStyle(new SolidFill({ color: ColorRGBA(255, 255, 255) }))
            .setSeriesBackgroundStrokeStyle(new SolidLine({
                thickness: 0
            }))
            .setPadding({ left: 10, right: 30, top: 10, bottom: 5 });
        // #endregion
        // #region ----- Chart Low Clustering extra config -----
        // Decide on an origin for DateTime axis.
        chartLowClust
            .setMouseInteractionRectangleFit(false)
            .setMouseInteractionRectangleZoom(false)
            .setMouseInteractionWheelZoom(false)
            .setMouseInteractionPan(false);

        //x-axis
        chartLowClust
            .getDefaultAxisX()
            .setMouseInteractions(false)
            .setAnimationScroll(false)
            .setTickStrategy(AxisTickStrategies.DateTime, (tickStrategy: DateTimeTickStrategy) => tickStrategy
                .setFormattingDay(
                    { year: 'numeric', month: 'long' },
                    { month: 'short', day: '2-digit' },
                    { hour: '2-digit', minute: '2-digit', hourCycle: 'h24' }
                )
                .setMajorTickStyle((tickStyle) => tickStyle
                    .setLabelFont(new FontSettings({ size: 11 }))
                )
                .setGreatTickStyle(emptyTick)
            )
            .setScrollStrategy(AxisScrollStrategies.progressive)
            .setStrokeStyle(new SolidLine({
                thickness: 2,
                fillStyle: new SolidFill({ color: ColorRGBA(225, 225, 225) })
            })).setTitle('Timestamp (UTC)').setTitleFont(new FontSettings({ size: 11 }));

        //y-axis
        chartLowClust
            .getDefaultAxisY()
            .setTickStrategy(AxisTickStrategies.Empty)
            .setChartInteractions(false)
            .setMouseInteractions(false).setTitle('Status').setTitleFont(new FontSettings({ size: 11 }))
            .setInterval({ start: 0.5, end: 3.5, stopAxisAfter: false });

        chartLowClust
            .getDefaultAxisY()
            .addCustomTick()
            .setValue(3)
            .setGridStrokeLength(1).setGridStrokeStyle(new SolidLine({ thickness: 1, fillStyle: new SolidFill({ color: ColorRGBA(232, 237, 241) }) }))
            .setMarker((tickMarker) =>
                tickMarker.setTextFont(new FontSettings({ size: 11 }))
            )
            .setTextFormatter((_) => 'Pending');

        chartLowClust
            .getDefaultAxisY()
            .addCustomTick()
            .setValue(2)
            .setGridStrokeLength(1).setGridStrokeStyle(new SolidLine({ thickness: 1, fillStyle: new SolidFill({ color: ColorRGBA(232, 237, 241) }) }))
            .setMarker((tickMarker) =>
                tickMarker.setTextFont(new FontSettings({ size: 11 }))
            )
            .setTextFormatter((_) => 'Completed');

        chartLowClust
            .getDefaultAxisY()
            .addCustomTick()
            .setValue(1)
            .setGridStrokeLength(1).setGridStrokeStyle(new SolidLine({ thickness: 1, fillStyle: new SolidFill({ color: ColorRGBA(232, 237, 241) }) }))
            .setMarker((tickMarker) =>
                tickMarker.setTextFont(new FontSettings({ size: 11 }))
            )
            .setTextFormatter((_) => 'Rejected');

        //cursor
        chartLowClust.setCursor((cursor) =>
            cursor.setTickMarkerXVisible(false).setTickMarkerYVisible(false)
        );

        // #endregion
        // #region ----- Chart Mid Clustering primary config -----
        const chartMidClust = lightningChart(license)
            .ChartXY({
                theme: Themes.light,
                // Set the chart into a div with id, 'target'.
                // Chart's size will automatically adjust to div's size.
                container: 'mid-clustering-chart',
                defaultAxisX: {
                    type: 'linear-highPrecision'
                }
            })
            .setTitle('Mid-Frequency')
            .setTitleFillStyle(new SolidFill({ color: ColorRGBA(48, 50, 53) }))
            .setTitleFont(new FontSettings({ size: 12, style: 'bold' }))
            .setTitleMargin({ left: 25 })
            .setBackgroundFillStyle(new SolidFill({ color: ColorRGBA(255, 255, 255) }))
            .setSeriesBackgroundStrokeStyle(new SolidLine({
                thickness: 0
            }))
            .setPadding({ left: 10, right: 30, top: 10, bottom: 5 });
        // #endregion
        // #region ----- Chart Mid Clustering extra config -----
        // Decide on an origin for DateTime axis.
        chartMidClust
            .setMouseInteractionRectangleFit(false)
            .setMouseInteractionRectangleZoom(false)
            .setMouseInteractionWheelZoom(false)
            .setMouseInteractionPan(false);

        //x-axis
        chartMidClust
            .getDefaultAxisX()
            .setMouseInteractions(false)
            .setAnimationScroll(false)
            .setTickStrategy(AxisTickStrategies.DateTime, (tickStrategy: DateTimeTickStrategy) => tickStrategy
                .setFormattingDay(
                    { year: 'numeric', month: 'long' },
                    { month: 'short', day: '2-digit' },
                    { hour: '2-digit', minute: '2-digit', hourCycle: 'h24' }
                )
                .setMajorTickStyle((tickStyle) => tickStyle
                    .setLabelFont(new FontSettings({ size: 11 }))
                )
                .setGreatTickStyle(emptyTick)
            )
            .setScrollStrategy(AxisScrollStrategies.progressive)
            .setStrokeStyle(new SolidLine({
                thickness: 2,
                fillStyle: new SolidFill({ color: ColorRGBA(225, 225, 225) })
            })).setTitle('Timestamp (UTC)').setTitleFont(new FontSettings({ size: 11 }));

        //y-axis
        chartMidClust
            .getDefaultAxisY()
            .setTickStrategy(AxisTickStrategies.Empty)
            .setChartInteractions(false)
            .setMouseInteractions(false).setTitle('Status').setTitleFont(new FontSettings({ size: 11 }))
            .setInterval({ start: 0.5, end: 3.5, stopAxisAfter: false });

        chartMidClust
            .getDefaultAxisY()
            .addCustomTick()
            .setValue(3)
            .setGridStrokeLength(1).setGridStrokeStyle(new SolidLine({ thickness: 1, fillStyle: new SolidFill({ color: ColorRGBA(232, 237, 241) }) }))
            .setMarker((tickMarker) =>
                tickMarker.setTextFont(new FontSettings({ size: 11 }))
            )
            .setTextFormatter((_) => 'Pending');

        chartMidClust
            .getDefaultAxisY()
            .addCustomTick()
            .setValue(2)
            .setGridStrokeLength(1).setGridStrokeStyle(new SolidLine({ thickness: 1, fillStyle: new SolidFill({ color: ColorRGBA(232, 237, 241) }) }))
            .setMarker((tickMarker) =>
                tickMarker.setTextFont(new FontSettings({ size: 11 }))
            )
            .setTextFormatter((_) => 'Completed');

        chartMidClust
            .getDefaultAxisY()
            .addCustomTick()
            .setValue(1)
            .setGridStrokeLength(1).setGridStrokeStyle(new SolidLine({ thickness: 1, fillStyle: new SolidFill({ color: ColorRGBA(232, 237, 241) }) }))
            .setMarker((tickMarker) =>
                tickMarker.setTextFont(new FontSettings({ size: 11 }))
            )
            .setTextFormatter((_) => 'Rejected');

        //cursor
        chartMidClust.setCursor((cursor) =>
            cursor.setTickMarkerXVisible(false).setTickMarkerYVisible(false)
        );

        // #endregion
        // #endregion ----- Config -----

        // #region ----- Spectrogram and potential detections -----

        const refreshClusteringEventsRef = async (originStartDate, originEndDate, projectId, vesselId, deviceId) => {

            // Parse the input date string
            const inputStartDate = new Date(new Date(originStartDate).getTime() - userOffset);

            // Format the date into "YYYY-MM-DDTHH:mm:ssZ" format
            const sDyear = inputStartDate.getUTCFullYear();
            const sDmonth = String(inputStartDate.getUTCMonth() + 1).padStart(2, '0');
            const sDday = String(inputStartDate.getUTCDate()).padStart(2, '0');
            const sDhours = String(inputStartDate.getUTCHours()).padStart(2, '0');
            const sDminutes = String(inputStartDate.getUTCMinutes()).padStart(2, '0');
            const sDseconds = String(inputStartDate.getUTCSeconds()).padStart(2, '0');

            const startDate = `${sDyear}-${sDmonth}-${sDday}T${sDhours}:${sDminutes}:${sDseconds}Z`;

            // Parse the input date string
            const inputEndDate = new Date(new Date(originEndDate).getTime() - userOffset);

            // Format the date into "YYYY-MM-DDTHH:mm:ssZ" format
            const eDyear = inputEndDate.getUTCFullYear();
            const eDmonth = String(inputEndDate.getUTCMonth() + 1).padStart(2, '0');
            const eDday = String(inputEndDate.getUTCDate()).padStart(2, '0');
            const eDhours = String(inputEndDate.getUTCHours()).padStart(2, '0');
            const eDminutes = String(inputEndDate.getUTCMinutes()).padStart(2, '0');
            const eDseconds = String(inputEndDate.getUTCSeconds()).padStart(2, '0');

            const endDate = `${eDyear}-${eDmonth}-${eDday}T${eDhours}:${eDminutes}:${eDseconds}Z`;

            try {
                const detectionRq = {
                    "type": "mid",
                    "startdate": startDate,
                    "enddate": endDate,
                    "projectId": projectId,
                    "vesselId": vesselId,
                    "deviceId": deviceId
                };

                const raw = await DetectionServices.GetClusterChart(detectionRq);

                if (!!raw) {
                    let result = raw.data;
                    // #region ----- API call clustering event ----- 
                    // Data for the plotting
                    chartMidClust
                        .getDefaultAxisX()
                        .setInterval({
                            start: new Date(startDate).getTime() + userOffset,
                            end: new Date(endDate).getTime() + userOffset
                        });

                    chartMidClust
                        .setCursorMode('show-nearest');

                    midPointsRef.current.forEach(item => {
                        item.clear();
                    });

                    let items = [];

                    if ((result.pending && Array.isArray(result.pending) && result.pending.length > 0) ||
                        (result.confirmed && Array.isArray(result.confirmed) && result.confirmed.length > 0) ||
                        (result.rejected && Array.isArray(result.rejected) && result.rejected.length > 0)) {

                        chartMidClust
                            .setCursorFormatting((chart, hit) => [
                                ['Timestamp (UTC)', '', `${moment(new Date(hit.x)).format("YYYY-MM-DD HH:mm:ss")}`],
                                ['Status', '', `${(hit.axisY.formatValue(hit.y) === "2.00" ? "Confirmed" : (hit.axisY.formatValue(hit.y) === "1.00" ? "Rejected" : "Pending"))}`]
                            ]);
                    }

                    if (result.pending && Array.isArray(result.pending) && result.pending.length > 0) {
                        // Loop through the "pending" array
                        result.pending.forEach(pendingItem => {
                            // Access pending properties
                            const pendingDetectionId = pendingItem.detectionId;
                            const pendingStartDate = pendingItem.startDate;
                            const pendingSpeciesGroup = pendingItem.speciesGroup;

                            const pendingMidClusterSeriesMain = chartMidClust
                                .addPointSeries({ pointShape: PointShape.Diamond });

                            //onclick point
                            pendingMidClusterSeriesMain
                                .onMouseClick(onMouseDetectionClick)

                            const pendingMidClusterSeries = pendingMidClusterSeriesMain
                                .setPointFillStyle(new IndividualPointFill({ color: ColorRGBA(249, 198, 6) }))
                                .setName(pendingDetectionId)
                                .setPointSize(10);

                            items.push(pendingMidClusterSeries);

                            // Your logic here for each pending item
                            // For example, you can create a new object and push it to the pendingMidClusterData array
                            pendingMidClusterSeries.add(
                                [
                                    {
                                        x: new Date(pendingStartDate).getTime() + userOffset,
                                        y: 3
                                    }
                                ]);
                        });
                    }

                    if (result.confirmed && Array.isArray(result.confirmed) && result.confirmed.length > 0) {
                        // Loop through the "confirmed" array
                        result.confirmed.forEach(confirmedItem => {
                            // Access confirmed properties
                            const confirmedDetectionId = confirmedItem.detectionId;
                            const confirmedStartDate = confirmedItem.startDate;
                            const confirmedSpeciesGroup = confirmedItem.speciesGroup;

                            const confirmedMidClusterSeriesMain = chartMidClust
                                .addPointSeries({ pointShape: PointShape.Circle });

                            //onclick point
                            confirmedMidClusterSeriesMain
                                .onMouseClick(onMouseDetectionClick)

                            const confirmedMidClusterSeries = confirmedMidClusterSeriesMain
                                .setPointFillStyle(new IndividualPointFill({ color: ColorRGBA(0, 177, 143) }))
                                .setName(confirmedDetectionId)
                                .setPointSize(10);

                            items.push(confirmedMidClusterSeries);

                            // Your logic here for each confirmed item
                            // For example, you can create a new object and push it to the confirmedMidClusterData array
                            confirmedMidClusterSeries.add(
                                [
                                    {
                                        x: new Date(confirmedStartDate).getTime() + userOffset,
                                        y: 2
                                    }
                                ]);
                        });
                    }

                    if (result.rejected && Array.isArray(result.rejected) && result.rejected.length > 0) {
                        // Loop through the "rejected" array
                        result.rejected.forEach(rejectedItem => {
                            // Access rejected properties
                            const rejectedDetectionId = rejectedItem.detectionId;
                            const rejectedStartDate = rejectedItem.startDate;
                            const rejectedSpeciesGroup = rejectedItem.speciesGroup;

                            const rejectedMidClusterSeriesMain = chartMidClust
                                .addPointSeries({ pointShape: PointShape.Triangle });

                            //onclick point
                            rejectedMidClusterSeriesMain
                                .onMouseClick(onMouseDetectionClick)

                            const rejectedMidClusterSeries = rejectedMidClusterSeriesMain
                                .setPointFillStyle(new IndividualPointFill({ color: ColorRGBA(223, 51, 18) }))
                                .setName(rejectedDetectionId)
                                .setPointSize(10);

                            items.push(rejectedMidClusterSeries);

                            // Your logic here for each rejected item
                            // For example, you can create a new object and push it to the rejectedMidClusterData array
                            rejectedMidClusterSeries.add(
                                [
                                    {
                                        x: new Date(rejectedStartDate).getTime() + userOffset,
                                        y: 1
                                    }
                                ]);
                        });
                    }

                    midPointsRef.current = items;

                    //#region Mid-Frequency overlay
                    midOverlayRef.current.forEach(item => {
                        item.dispose();
                    });

                    let overlays = [];

                    let selectedHighlight = highlightCETD1;
                    let selectedStroke = strokeCETD1;

                    // Check if result.cluster exists and is not null or empty
                    if (result.cluster && Array.isArray(result.cluster) && result.cluster.length > 0) {
                        // Loop through the cluster list
                        result.cluster.forEach(clusterItem => {
                            // Access cluster properties
                            const clusterId = clusterItem.clusterId;
                            const clusterStartDate = clusterItem.startDate;
                            const clusterEndDate = clusterItem.endDate;
                            let newClusterStartDate = new Date(clusterStartDate).getTime() + userOffset;
                            let newClusterEndDate = new Date(clusterEndDate).getTime() + userOffset;

                            const alertOverlayMidCust = chartMidClust
                                .getDefaultAxisX()
                                .addBand(false)
                                .setMouseInteractions(true)
                                .setValueStart(newClusterStartDate)
                                .setValueEnd(newClusterEndDate)
                                .setFillStyle(selectedHighlight)
                                .setStrokeStyle(new SolidLine({
                                    thickness: 1,
                                    fillStyle: selectedStroke
                                }))
                                .setName(clusterId);

                            //#region - reassign band positions during certain unwanted mouse interactions.
                            alertOverlayMidCust.onMouseDrag((band) => {
                                band.setValueStart(newClusterStartDate).setValueEnd(newClusterEndDate)
                            });

                            alertOverlayMidCust.onTouchStart((band) => {
                                band.setValueStart(newClusterStartDate).setValueEnd(newClusterEndDate)
                            });

                            alertOverlayMidCust.onTouchMove((band) => {
                                band.setValueStart(newClusterStartDate).setValueEnd(newClusterEndDate)
                            });

                            alertOverlayMidCust.onTouchEnd((band) => {
                                band.setValueStart(newClusterStartDate).setValueEnd(newClusterEndDate)
                            });
                            //#endregion

                            alertOverlayMidCust.onMouseMove(onMouseMoveClust);
                            alertOverlayMidCust.onMouseLeave(onMouseLeaveClust);
                            alertOverlayMidCust.onMouseClick(onMouseClickClust);

                            overlays.push(alertOverlayMidCust);
                        });

                        midOverlayRef.current = overlays;

                    } else {
                        //console.log("No mid cluster data available");
                    }

                    //#endregion

                    //#endregion
                }
            }
            catch (error) {
                // Display error message for network errors
                console.error('Error get mid clustering chart:', error);
            }

            try {
                const detectionRq = {
                    "type": "low",
                    "startdate": startDate,
                    "enddate": endDate,
                    "projectId": projectId,
                    "vesselId": vesselId,
                    "deviceId": deviceId
                };

                const raw = await DetectionServices.GetClusterChart(detectionRq);

                if (!!raw) {
                    let result = raw.data;
                    // #region ----- API call clustering event ----- 
                    // Data for the plotting
                    chartLowClust
                        .getDefaultAxisX()
                        .setInterval({
                            start: new Date(startDate).getTime() + userOffset,
                            end: new Date(endDate).getTime() + userOffset
                        });

                    chartLowClust
                        .setCursorMode('show-nearest');

                    lowPointsRef.current.forEach(item => {
                        item.clear();
                    });

                    let items = [];

                    if ((result.pending && Array.isArray(result.pending) && result.pending.length > 0) ||
                        (result.confirmed && Array.isArray(result.confirmed) && result.confirmed.length > 0) ||
                        (result.rejected && Array.isArray(result.rejected) && result.rejected.length > 0)) {
                        chartLowClust
                            .setCursorFormatting((chart, hit) => [
                                ['Timestamp (UTC)', '', `${moment(new Date(hit.x)).format("YYYY-MM-DD HH:mm:ss")}`],
                                ['Status', '', `${(hit.axisY.formatValue(hit.y) === "2.00" ? "Confirmed" : (hit.axisY.formatValue(hit.y) === "1.00" ? "Rejected" : "Pending"))}`]
                            ]);
                    }

                    if (result.pending && Array.isArray(result.pending) && result.pending.length > 0) {
                        // Loop through the "pending" array
                        result.pending.forEach(pendingItem => {
                            // Access pending properties
                            const pendingDetectionId = pendingItem.detectionId;
                            const pendingStartDate = pendingItem.startDate;
                            const pendingSpeciesGroup = pendingItem.speciesGroup;

                            const pendingLowClusterSeriesMain = chartLowClust
                                .addPointSeries({ pointShape: PointShape.Diamond });

                            //onclick point
                            pendingLowClusterSeriesMain
                                .onMouseClick(onMouseDetectionClick)

                            const pendingLowClusterSeries = pendingLowClusterSeriesMain
                                .setPointFillStyle(new IndividualPointFill({ color: ColorRGBA(249, 198, 6) }))
                                .setName(pendingDetectionId)
                                .setPointSize(10);

                            items.push(pendingLowClusterSeries);

                            // Your logic here for each pending item
                            // For example, you can create a new object and push it to the pendingLowClusterData array
                            pendingLowClusterSeries.add(
                                [
                                    {
                                        x: new Date(pendingStartDate).getTime() + userOffset,
                                        y: 3
                                    }
                                ]);
                        });
                    }

                    if (result.confirmed && Array.isArray(result.confirmed) && result.confirmed.length > 0) {
                        // Loop through the "confirmed" array
                        result.confirmed.forEach(confirmedItem => {
                            // Access confirmed properties
                            const confirmedDetectionId = confirmedItem.detectionId;
                            const confirmedStartDate = confirmedItem.startDate;
                            const confirmedSpeciesGroup = confirmedItem.speciesGroup;

                            const confirmedLowClusterSeriesMain = chartLowClust
                                .addPointSeries({ pointShape: PointShape.Circle })

                            //onclick point
                            confirmedLowClusterSeriesMain
                                .onMouseClick(onMouseDetectionClick)

                            const confirmedLowClusterSeries = confirmedLowClusterSeriesMain
                                .setPointFillStyle(new IndividualPointFill({ color: ColorRGBA(0, 177, 143) }))
                                .setName(confirmedDetectionId)
                                .setPointSize(10);

                            items.push(confirmedLowClusterSeries);

                            // Your logic here for each confirmed item
                            // For example, you can create a new object and push it to the confirmedLowClusterData array
                            confirmedLowClusterSeries.add(
                                [
                                    {
                                        x: new Date(confirmedStartDate).getTime() + userOffset,
                                        y: 2
                                    }
                                ]);
                        });
                    }

                    if (result.rejected && Array.isArray(result.rejected) && result.rejected.length > 0) {
                        // Loop through the "rejected" array
                        result.rejected.forEach(rejectedItem => {
                            // Access rejected properties
                            const rejectedDetectionId = rejectedItem.detectionId;
                            const rejectedStartDate = rejectedItem.startDate;
                            const rejectedSpeciesGroup = rejectedItem.speciesGroup;

                            const rejectedLowClusterSeriesMain = chartLowClust
                                .addPointSeries({ pointShape: PointShape.Triangle });

                            //onclick point
                            rejectedLowClusterSeriesMain
                                .onMouseClick(onMouseDetectionClick)

                            const rejectedLowClusterSeries = rejectedLowClusterSeriesMain
                                .setPointFillStyle(new IndividualPointFill({ color: ColorRGBA(223, 51, 18) }))
                                .setName(rejectedDetectionId)
                                .setPointSize(10);

                            items.push(rejectedLowClusterSeries);

                            // Your logic here for each rejected item
                            // For example, you can create a new object and push it to the rejectedLowClusterData array
                            rejectedLowClusterSeries.add(
                                [
                                    {
                                        x: new Date(rejectedStartDate).getTime() + userOffset,
                                        y: 1
                                    }
                                ]);
                        });
                    }

                    lowPointsRef.current = items;

                    //#region Low-Frequency overlay
                    lowOverlayRef.current.forEach(item => {
                        item.dispose();
                    });

                    let overlays = [];

                    let selectedHighlight = highlightCETD1;
                    let selectedStroke = strokeCETD1;

                    if (document.getElementById('ddlSelectedOption')) {

                        let option = document.getElementById('ddlSelectedOption').innerHTML;

                        if (option === 'CET-D1') {
                            selectedHighlight = highlightCETD1;
                            selectedStroke = strokeCETD1;
                        }
                        else if (option === 'CET-D2') {
                            selectedHighlight = highlightCETD2;
                            selectedStroke = strokeCETD2;
                        }
                        else if (option === 'CET-D3') {
                            selectedHighlight = highlightCETD3;
                            selectedStroke = strokeCETD3;
                        }
                        else if (option === 'CET-D4') {
                            selectedHighlight = highlightCETD4;
                            selectedStroke = strokeCETD4;
                        }
                    }

                    // Check if result.cluster exists and is not null or empty
                    if (result.cluster && Array.isArray(result.cluster) && result.cluster.length > 0) {
                        // Loop through the cluster list
                        result.cluster.forEach(clusterItem => {
                            // Access cluster properties
                            const clusterId = clusterItem.clusterId;
                            const clusterStartDate = clusterItem.startDate;
                            const clusterEndDate = clusterItem.endDate;
                            let newClusterStartDate = new Date(clusterStartDate).getTime() + userOffset;
                            let newClusterEndDate = new Date(clusterEndDate).getTime() + userOffset;

                            const alertOverlayLowCust = chartLowClust
                                .getDefaultAxisX()
                                .addBand(false)
                                .setMouseInteractions(true)
                                .setValueStart(newClusterStartDate)
                                .setValueEnd(newClusterEndDate)
                                .setFillStyle(selectedHighlight)
                                .setStrokeStyle(new SolidLine({
                                    thickness: 1,
                                    fillStyle: selectedStroke
                                }))
                                .setName(clusterId);

                            //#region - reassign band positions during certain unwanted mouse interactions.
                            alertOverlayLowCust.onMouseDrag((band) => {
                                band.setValueStart(newClusterStartDate).setValueEnd(newClusterEndDate)
                            });

                            alertOverlayLowCust.onTouchStart((band) => {
                                band.setValueStart(newClusterStartDate).setValueEnd(newClusterEndDate)
                            });

                            alertOverlayLowCust.onTouchMove((band) => {
                                band.setValueStart(newClusterStartDate).setValueEnd(newClusterEndDate)
                            });

                            alertOverlayLowCust.onTouchEnd((band) => {
                                band.setValueStart(newClusterStartDate).setValueEnd(newClusterEndDate)
                            });
                            //#endregion

                            alertOverlayLowCust.onMouseMove(onMouseMoveClust);
                            alertOverlayLowCust.onMouseLeave(onMouseLeaveClust);
                            alertOverlayLowCust.onMouseClick(onMouseClickClust);

                            overlays.push(alertOverlayLowCust);
                        });

                    } else {
                        //console.log("No low cluster data available");
                    }

                    lowOverlayRef.current = overlays;

                    // #endregion

                    // #endregion
                }
            }
            catch (error) {
                // Display error message for network errors
                console.error('Error get low clustering chart:', error);
            }
        };

        handleRefreshClusteringEventsRef.current = refreshClusteringEventsRef;

        if (!!_auth) {
            if (_auth.license !== undefined && _auth.license !== 'undefined' && _auth.license !== '') {

                const inputString = _auth.license;
                const parts = inputString.split('-');
                if (parts.length === 3) {
                    const licenseKey = parts[2]; // This will give you the third part after splitting
                    const canvasElements = document.querySelectorAll('[data-lcjs-id="' + licenseKey + '"]');

                    canvasElements.forEach(canvasElement => {
                        canvasElement.remove(); // Removes each matched element from the DOM
                    });
                }
            }
        }

        return () => { };

        // #endregion
    }, []);

    const handleStartDateChange = (startDate) => {
        if (detectionContext.SelectedClusterDateRange[1] !== null && startDate >= detectionContext.SelectedClusterDateRange[1]) {
            handleError('Warning', 'Invalid date range');
            detectionContext.SetSelectedClusterDateRange([null, detectionContext.SelectedClusterDateRange[1]]);
            return;
        }

        detectionContext.SetSelectedClusterDateRange([startDate, detectionContext.SelectedClusterDateRange[1]]);
    };

    const handleEndDateChange = (endDate) => {
        if (detectionContext.SelectedClusterDateRange[0] !== null && endDate <= detectionContext.SelectedClusterDateRange[0]) {
            handleError('Warning', 'Invalid date range');
            detectionContext.SetSelectedClusterDateRange([detectionContext.SelectedClusterDateRange[0], null]);
            return;
        }

        detectionContext.SetSelectedClusterDateRange([detectionContext.SelectedClusterDateRange[0], endDate]);
    };

    const handleResetDateRange = () => {
        if (detectionContext.SelectedClusterDateRange[0] !== null || detectionContext.SelectedClusterDateRange[1] !== null) {
            detectionContext.SetSelectedClusterDateRange([null, null]);
        }
    };

    useEffect(() => {
        let refreshFlag = (!(detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === ''));

        if (refreshFlag) {
            if (detectionContext.SelectedClusterDateRange[0] !== null && detectionContext.SelectedClusterDateRange[1] !== null) {
                handleRefreshClusteringEventsRef.current(detectionContext.SelectedClusterDateRange[0], detectionContext.SelectedClusterDateRange[1], detectionContext.DetectionConfig.projectid, detectionContext.DetectionConfig.vesselid, detectionContext.DetectionConfig.deviceid);
            }
            else if (detectionContext.SelectedClusterDateRange[0] === null && detectionContext.SelectedClusterDateRange[1] === null) {
                refreshClusterChart(detectionContext.DetectionConfig.projectid, detectionContext.DetectionConfig.vesselid, detectionContext.DetectionConfig.deviceid);
            }
        }

        detectionContext.SetSaveValidationClicked(false);

    }, [detectionContext, detectionContext.SelectedFilter, detectionContext.SelectedClusterDateRange, detectionContext.DetectionConfig]);

    useEffect(() => {

        if (detectionContext.SaveValidationClicked) {
            let refreshFlag = (!(detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === ''));

            if (refreshFlag) {
                if (detectionContext.SelectedClusterDateRange[0] !== null && detectionContext.SelectedClusterDateRange[1] !== null) {
                    handleRefreshClusteringEventsRef.current(detectionContext.SelectedClusterDateRange[0], detectionContext.SelectedClusterDateRange[1], detectionContext.DetectionConfig.projectid, detectionContext.DetectionConfig.vesselid, detectionContext.DetectionConfig.deviceid);
                }
                else if (detectionContext.SelectedClusterDateRange[0] === null && detectionContext.SelectedClusterDateRange[1] === null) {
                    refreshClusterChart(detectionContext.DetectionConfig.projectid, detectionContext.DetectionConfig.vesselid, detectionContext.DetectionConfig.deviceid);
                }
            }

            detectionContext.SetSaveValidationClicked(false);
        }

    }, [detectionContext, detectionContext.SaveValidationClicked, detectionContext.SelectedClusterDateRange, detectionContext.DetectionConfig]);

    const refreshClusterChart = (projectId, vesselId, deviceId) => {
        let localDt = new Date();
        let userOffset = localDt.getTimezoneOffset() * 60 * 1000;
        let originStartDate = sub(localDt, { hours: 1, minutes: 15 });
        let originEndDate = localDt;

        //make it UTC today's date
        handleRefreshClusteringEventsRef.current(new Date(new Date(originStartDate).getTime() + userOffset), new Date(new Date(originEndDate).getTime() + userOffset), projectId, vesselId, deviceId);
    }

    useEffect(() => {

        const _auth = JSON.parse(sessionStorage.getItem("auth"));
        let interval = 1;

        if (!!_auth) {
            interval = _auth.clusterChartRefreshInterval;
        }

        // Set interval to trigger the function every N minutes (in milliseconds)
        const intervalId = setInterval(() => {
            detectionContext.SetSelectedClusterDateRange((prevDateRange) => {
                if (prevDateRange[0] === null && prevDateRange[1] === null) {
                    const startElement = document.getElementById('dtClusterStart');
                    const endElement = document.getElementById('dtClusterEnd');

                    if (startElement && endElement) {
                        const startValue = startElement.value;
                        const endValue = endElement.value;

                        let refreshFlag = (!(detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === ''));

                        if (refreshFlag) {
                            // Check the diagram whether unfreeze + Simplified check for empty or undefined values
                            if (!startValue && !endValue) {
                                refreshClusterChart(detectionContext.DetectionConfig.projectid, detectionContext.DetectionConfig.vesselid, detectionContext.DetectionConfig.deviceid);
                            }
                        }
                    }
                }
                return prevDateRange; // Return the unchanged value
            });
        }, interval * 60 * 1000); // interval is the number of minutes

        if (detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === '') {
            detectionContext.SetShowDetectionConfigPopup(true);
        }

        // Cleanup the interval on component unmount
        return () => clearInterval(intervalId);
    }, []);

    const handleError = (errorType, errorMessage) => {
        authContext.HandleError(errorType, errorMessage);
    }

    const handlePopupClick = (e) => {
        e.preventDefault(); // Prevent default anchor behavior
        detectionContext.SetShowDetectionConfigPopup(true);
    };

    return (
        <>
            <div className="detection-management-container position-relative">
                <NewDetectionConfig />
                <div className="detection-container">
                    <div className="spectrogram-section">
                        <div className="ms-3 me-3 mb-3">
                            <div className="detection-filter-section flex-container flex-wrap justify-content-between">
                                <a className="nav-exactlink link invisible" href="/#"><span><i className="fa-solid fa-wave-triangle"></i>&nbsp;<span>More Detections</span></span></a>
                                <div className="subsection-title text-center col-rps-light">Clustering Events</div>
                                <a className="nav-exactlink link" href="/#/detections"><span><i className="fa-solid fa-wave-triangle"></i>&nbsp;<span>More Detections</span></span></a>
                            </div>
                            <div className="text-center">
                                <a className="nav-exactlink link" href="/#" onClick={handlePopupClick}><span><i className="fa-solid fa-angles-down"></i>&nbsp;<span>{detectionContext.DetectionConfig.projectid === '' || detectionContext.DetectionConfig.vesselid === '' || detectionContext.DetectionConfig.deviceid === '' ? 'Preset Event Settings' : detectionContext.DetectionConfig.projectname + ' / ' + detectionContext.DetectionConfig.vesselname + ' / ' + detectionContext.DetectionConfig.devicename}</span></span></a>
                            </div>
                        </div>
                        <div className="detection-filter-section flex-container flex-wrap ms-3 me-3 mb-3">
                            <InputGroup className="date-picker-input-group">
                                <DatePicker
                                    id="dtClusterStart"
                                    className="date-picker-input"
                                    placeholder="Start Date"
                                    format="yyyy-MM-dd HH:mm"
                                    calendarDefaultDate={startOfDay(utcNow)}
                                    value={detectionContext.SelectedClusterDateRange[0]} // Bind start date to state
                                    onChange={handleStartDateChange} // Update start date
                                    cleanable={false}
                                />
                                <InputGroup.Addon className="rs-input-group-addon-text">to</InputGroup.Addon>
                                <DatePicker
                                    id="dtClusterEnd"
                                    className="date-picker-input"
                                    placeholder="End Date"
                                    format="yyyy-MM-dd HH:mm"
                                    calendarDefaultDate={endOfDay(utcNow)}
                                    value={detectionContext.SelectedClusterDateRange[1]} // Bind end date to state
                                    onChange={handleEndDateChange} // Update end date 
                                    cleanable={false}
                                />
                                <InputGroup.Addon className="rs-input-group-addon-text">
                                    <i className="fa-light fa-xmark cursor-pointer" onClick={handleResetDateRange}></i>
                                </InputGroup.Addon>
                            </InputGroup>
                        </div>
                        <div id="mid-clustering-chart" className="spectrogram-row spectrogram-content spectrogram-mid-clustering-container spectrogram-container ms-3 me-3 mb-3"></div>
                        <div id="low-clustering-chart" className="spectrogram-row spectrogram-content spectrogram-low-clustering-container spectrogram-container ms-3 me-3"></div>
                        <div id="clustering-chart-tooltip" className="pos-absolute d-none clustering-chart-tooltip">
                            <div className="pb-1">Click here to view more on detection events.</div>
                            <div><span>Cluster starts:</span><span id="clustering-chart-tooltip-start" className="float-right"></span></div>
                            <div><span>Cluster ends:</span><span id="clustering-chart-tooltip-end" className="float-right"></span></div>
                        </div>
                    </div>
                    </div>
            </div>
        </>
    );
}

export default DetectionManagement;
